import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { getApiTokensClient } from "sj-ui-toolkit/src/client/svcApiTokens";
import { humanDate, humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ENTITLEMENT_OPTIONS = [
  {
    label: "Read",
    value: "aspen_read",
    description: "Used to fetch data collected and processed by Aspen",
  },
  {
    label: "Write",
    value: "aspen_write",
    description: "Used to connect external data sources to Aspen",
  },
];

// github_action and guardian are legacy write-capable entitlements, kept so
// previously-issued tokens still show up in the list.
const WRITE_ENTITLEMENTS = ["github_action", "guardian", "aspen_write"];
const READ_ENTITLEMENTS = ["aspen_read"];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AspenConnection = () => {
  useBreadcrumb({
    label: "Aspen Integrations",
    route: AdminAppRoutes.routes.connections.routes.aspen.route,
    display: "page",
  });

  const toast = useRef<Toast>(null);
  const apiTokensClient = getApiTokensClient();

  const [apiTokens, setApiTokens] = useState<
    {
      tenant?: string;
      uuid?: string;
      label?: string;
      createdAt?: string;
      resolvedAt?: string;
      access: "Read" | "Write" | "Read/Write";
    }[]
  >([]);
  const [newTokenSecret, setNewTokenSecret] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [tokenLabel, setTokenLabel] = useState("");
  const [selectedEntitlements, setSelectedEntitlements] = useState<string[]>(["aspen_write"]);

  const showLocalToast = useCallback(
    (
      severity: "success" | "info" | "warn" | "error",
      summary: string,
      detail: string,
      life = 3000
    ) => {
      toast.current?.show({ severity, summary, detail, life });
    },
    []
  );

  const loadTokens = useCallback(async () => {
    setIsLoading(true);
    try {
      const [writeResult, readResult] = await Promise.all([
        apiTokensClient.listApiTokensWithEntitlements(WRITE_ENTITLEMENTS),
        apiTokensClient.listApiTokensWithEntitlements(READ_ENTITLEMENTS),
      ]);
      if (writeResult.error) throw writeResult.error;
      if (readResult.error) throw readResult.error;

      // A token can hold both a write and a read entitlement; combine those
      // into a single "Read/Write" row instead of picking one.
      const merged = new Map<string, (typeof apiTokens)[number]>();
      for (const token of readResult.data?.apiTokens ?? []) {
        merged.set(token.uuid ?? "", { ...token, access: "Read" });
      }
      for (const token of writeResult.data?.apiTokens ?? []) {
        const existing = merged.get(token.uuid ?? "");
        merged.set(token.uuid ?? "", {
          ...token,
          access: existing ? "Read/Write" : "Write",
        });
      }
      setApiTokens([...merged.values()]);
    } catch {
      showLocalToast("error", "Error", "Failed to load keys.", 5000);
    } finally {
      setIsLoading(false);
    }
  }, [showLocalToast, apiTokensClient]);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const sortedTokens = useMemo(
    () =>
      [...apiTokens].sort(
        (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
      ),
    [apiTokens]
  );

  const handleGenerate = async () => {
    if (!tokenLabel.trim() || selectedEntitlements.length === 0) return;

    setIsLoading(true);
    try {
      const { data, error } = await apiTokensClient.issueApiToken(
        tokenLabel.trim(),
        selectedEntitlements
      );
      if (error) throw error;

      setNewTokenSecret(data?.secret ?? "");
      setTokenLabel("");
      setSelectedEntitlements(["aspen_write"]);
      setIsCreateModalVisible(false);
      await loadTokens();
      showLocalToast("success", "Success", "Key created successfully");
    } catch {
      showLocalToast("error", "Error", "Failed to create key. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    if (text) {
      navigator.clipboard.writeText(text);
      showLocalToast("info", "Copied", "Key copied to clipboard");
    }
  };

  const confirmDelete = (token: { uuid: string; label?: string }) => {
    confirmDialog({
      message: `Are you sure you want to delete "${token.label || token.uuid}"? This action cannot be undone.`,
      header: "Delete Key",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const { error } = await apiTokensClient.revokeApiToken(token.uuid);
          if (error) throw error;
          setApiTokens(prev => prev.filter(t => t.uuid !== token.uuid));
          showLocalToast("info", "Deleted", "Key deleted successfully");
        } catch {
          showLocalToast("error", "Error", "Failed to delete key");
        }
      },
    });
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="mb-4">
        <h1>Aspen Integrations</h1>
      </div>

      <Card>
        <div className="flex justify-between items-center">
          <div>
            <p className="m-0 text-secondary">
              These API keys can be used with the aspen-connector or for custom API integrations
              with Aspen Adapt.
            </p>
          </div>
          <Button
            label="Create Aspen Key"
            icon="pi pi-plus"
            onClick={() => setIsCreateModalVisible(true)}
            disabled={isLoading}
          />
        </div>

        {newTokenSecret && (
          <div className="mt-4">
            <strong>New Aspen Key (copy this now - it will not be shown again):</strong>
            <div className="p-inputgroup mt-2">
              <InputText value={newTokenSecret} readOnly />
              <Button
                icon="pi pi-copy"
                onClick={() => handleCopy(newTokenSecret)}
                tooltip="Copy to clipboard"
                tooltipOptions={{ position: "top", className: "whitespace-nowrap" }}
              />
              <Button
                icon="pi pi-times"
                onClick={() => setNewTokenSecret("")}
                className="p-button-secondary"
                tooltip="Dismiss"
                tooltipOptions={{ position: "top", className: "whitespace-nowrap" }}
              />
            </div>
          </div>
        )}

        <div className="mt-4">
          <DataTable
            value={sortedTokens}
            className="p-datatable-sm"
            tableClassName="dt-responsive-text"
            sortMode="single"
            loading={isLoading}
          >
            <Column
              field="label"
              header="Name"
              body={(rowData: { label?: string; uuid: string }) => rowData.label || rowData.uuid}
              sortable
            />
            <Column
              field="createdAt"
              header="Created At"
              body={(rowData: { createdAt?: string }) => {
                if (!rowData.createdAt) return "--";
                return (
                  <span title={humanDate(rowData.createdAt)}>
                    {humanDateWithoutTime(rowData.createdAt)}
                  </span>
                );
              }}
              sortable
            />
            <Column
              field="resolvedAt"
              header="Last Used"
              body={(rowData: { resolvedAt?: string }) => {
                if (!rowData.resolvedAt) return "Never";
                return (
                  <span title={humanDate(rowData.resolvedAt)}>
                    {humanDateWithoutTime(rowData.resolvedAt)}
                  </span>
                );
              }}
              sortable
            />
            <Column field="access" header="Access" sortable />
            <Column
              header="Action"
              body={(rowData: { uuid: string; label?: string }) => (
                <Button
                  label="Delete"
                  severity="danger"
                  text
                  size="small"
                  disabled={isLoading}
                  onClick={() => confirmDelete(rowData)}
                />
              )}
            />
          </DataTable>
          {!isLoading && sortedTokens.length === 0 && (
            <p className="text-secondary mt-3">No keys found. Create one to get started.</p>
          )}
        </div>
      </Card>

      <Dialog
        pt={{ root: { className: "border-none" } }}
        header="Create Aspen Key"
        visible={isCreateModalVisible}
        onHide={() => {
          setIsCreateModalVisible(false);
          setTokenLabel("");
          setSelectedEntitlements(["aspen_write"]);
        }}
        style={{ width: "40rem", maxWidth: "95vw" }}
        modal
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              label="Cancel"
              className="p-button-secondary"
              onClick={() => {
                setIsCreateModalVisible(false);
                setTokenLabel("");
                setSelectedEntitlements(["aspen_write"]);
              }}
              disabled={isLoading}
            />
            <Button
              label="Create"
              icon="pi pi-check"
              onClick={handleGenerate}
              disabled={isLoading || !tokenLabel.trim() || selectedEntitlements.length === 0}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <InputText
            value={tokenLabel}
            onChange={e => setTokenLabel(e.target.value)}
            placeholder="Key Name (e.g., Production Guardian Workflow)"
            className="w-full"
            maxLength={120}
            disabled={isLoading}
            autoFocus
          />
          <div>
            <label className="block mb-2">Access Level</label>
            <div className="flex flex-col gap-2">
              {ENTITLEMENT_OPTIONS.map(option => (
                <div key={option.value} className="flex items-center gap-2">
                  <Checkbox
                    inputId={`entitlement-${option.value}`}
                    name="tokenEntitlement"
                    value={option.value}
                    checked={selectedEntitlements.includes(option.value)}
                    onChange={e => {
                      setSelectedEntitlements(prev =>
                        e.checked ? [...prev, option.value] : prev.filter(v => v !== option.value)
                      );
                    }}
                    disabled={isLoading}
                  />
                  <label htmlFor={`entitlement-${option.value}`} className="body-sm">
                    {option.label}{" "}
                    <span className="body-xs text-secondary">({option.description})</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AspenConnection;
